import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HeroSearchPill from "../components/HeroSearchPill";
import { batchThumbs, searchPhotos } from "../lib/api";

const HAND_PICKED = [
  { key: "premier-creative", title: "Premier Creative", bg: "premium editorial fashion photography", nav: "premier creative editorial" },
  { key: "campaign-core", title: "Campaign Core", bg: "luxury lifestyle campaign photography", nav: "campaign core lifestyle" },
  { key: "visual-systems", title: "Visual Systems", bg: "brand identity design mockup studio", nav: "visual systems brand design" },
  { key: "unfiltered-archive", title: "Unfiltered Archive", bg: "black white archival photojournalism", nav: "unfiltered archive photojournalism" },
];

const CATEGORIES = [
  { title: "Abstract Geometry", sub: "Patterns, shapes, digital art", bg: "abstract geometry pattern digital art", nav: "abstract geometry pattern" },
  { title: "Majestic Wildlife", sub: "High-end animal portraits", bg: "majestic wildlife portrait", nav: "majestic wildlife" },
  { title: "Sustainable Nature", sub: "Eco-friendly, green environments", bg: "sustainable green forest nature", nav: "sustainable nature eco" },
  { title: "Future-Forward Tech", sub: "AI, robotics, data visuals", bg: "artificial intelligence robotics data", nav: "future tech ai robotics" },
  { title: "Immersive Backgrounds", sub: "Textures, gradients, mood-setters", bg: "abstract gradient texture background", nav: "immersive background texture" },
  { title: "Minimalist Architecture", sub: "Clean lines, modern structures", bg: "minimalist modern architecture building", nav: "minimalist architecture" },
  { title: "Cinematic Landscapes", sub: "Vast, moody environments", bg: "cinematic moody landscape mountains", nav: "cinematic landscape" },
  { title: "Urban Grit & Neon", sub: "City streets, night life, cyberpunk", bg: "urban neon cyberpunk city street night", nav: "urban neon cyberpunk" },
  { title: "Macro Details", sub: "Close-ups of textures, nature, or products", bg: "macro photography close up detail", nav: "macro details close up" },
  { title: "Surrealism & Dreamscapes", sub: "Creative, mind-bending art", bg: "surreal dreamscape creative art", nav: "surrealism dreamscape" },
  { title: "Human Emotion", sub: "Authentic portraits, raw connection", bg: "authentic emotional portrait candid", nav: "human emotion portrait" },
  { title: "Vintage Nostalgia", sub: "Film grain, retro vibes, archival style", bg: "vintage retro film grain photography", nav: "vintage nostalgia film" },
  { title: "Industrial Power", sub: "Machinery, engineering, strength", bg: "industrial machinery engineering factory", nav: "industrial power machinery" },
  { title: "Organic Textures", sub: "Wood, stone, water, natural patterns", bg: "organic wood stone water texture", nav: "organic textures natural" },
  { title: "Aerial Perspectives", sub: "Drone views, God-eye shots", bg: "aerial drone view landscape", nav: "aerial perspective drone" },
];

const TRENDING = [
  { title: "Quiet Luxury", sub: "Understated wealth, tonal palettes", bg: "quiet luxury minimal lifestyle", nav: "quiet luxury minimal" },
  { title: "Golden Hour Portraits", sub: "Warm-lit human stories", bg: "golden hour portrait sunset light", nav: "golden hour portrait" },
  { title: "Workspaces 2026", sub: "Modern hybrid work environments", bg: "modern hybrid workspace office 2026", nav: "workspaces modern office" },
  { title: "Analog Film Moods", sub: "Grainy, timeless, imperfect", bg: "analog film grain moody photography", nav: "analog film mood" },
];

// De-dupe queries across all sections into one backend call
const ALL_QUERIES = Array.from(new Set([
  "abstract art painting texture",
  ...HAND_PICKED.map((c) => c.bg),
  ...CATEGORIES.map((c) => c.bg),
  ...TRENDING.map((c) => c.bg),
]));

function toSearch(q) { return `/dashboard/search?q=${encodeURIComponent(q)}`; }

function Card({ item, thumb, size = "md", index = 0 }) {
  const src = thumb?.src?.medium || thumb?.src?.small || thumb?.src?.tiny;
  const heading = size === "sm" ? "text-lg md:text-xl" : size === "lg" ? "text-3xl md:text-4xl lg:text-5xl" : "text-xl md:text-2xl";
  return (
    <Link
      to={toSearch(item.nav)}
      data-testid={`cf-card-${item.key || item.title.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`}
      className="group block relative overflow-hidden bg-ink"
      style={{ aspectRatio: "5 / 4" }}
    >
      {src && (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          style={{ backgroundColor: thumb?.avg_color || "#111" }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
      <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
        {size === "lg" && (
          <div className="text-[10px] uppercase tracking-[0.35em] text-gold mb-1.5">— {String(index + 1).padStart(2, "0")}</div>
        )}
        <h3 className={`font-serif text-ivory leading-tight ${heading}`}>{item.title}</h3>
        {item.sub && <p className="mt-1 text-[11px] md:text-xs text-ivory/75 leading-snug line-clamp-2">{item.sub}</p>}
      </div>
    </Link>
  );
}

function SectionHeader({ eyebrow, title, desc }) {
  return (
    <div className="mb-6 md:mb-8">
      <div className="text-[11px] uppercase tracking-[0.35em] text-muted-foreground">{eyebrow}</div>
      <h2 className="font-serif text-3xl md:text-4xl text-ink mt-2 leading-tight">{title}</h2>
      {desc && <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-2xl">{desc}</p>}
    </div>
  );
}

export default function CuratedFrames() {
  const [thumbs, setThumbs] = useState({});
  const [hero, setHero] = useState(null);

  useEffect(() => {
    let cancelled = false;
    batchThumbs(ALL_QUERIES)
      .then((map) => {
        if (cancelled) return;
        setThumbs(map);
        setHero(map["abstract art painting texture"] || null);
      })
      .catch(() => {});
    // fallback: fetch hero directly if batch didn't include it
    searchPhotos({ query: "abstract art painting texture", per_page: 1, page: 1 })
      .then((d) => { if (!cancelled && !hero && d.photos?.[0]) setHero(d.photos[0]); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const heroSrc = hero?.src?.large || hero?.src?.medium;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        {/* ============ REDUCED HERO ============ */}
        <section data-testid="cf-hero" className="relative w-full overflow-hidden" style={{ height: "42vh", minHeight: "380px" }}>
          {heroSrc && (
            <img
              src={heroSrc}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ backgroundColor: hero?.avg_color || "#111" }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70" />
          <div className="relative h-full max-w-[1600px] mx-auto px-6 md:px-10 flex flex-col items-center justify-center text-center">
            <div className="text-[11px] uppercase tracking-[0.35em] text-gold mb-2">Elite Collection</div>
            <h1 data-testid="cf-title" className="font-serif text-ivory text-5xl md:text-6xl lg:text-7xl leading-[1.02] drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">Curated Frames</h1>
            <p className="mt-3 text-ivory/85 text-base md:text-lg max-w-xl">High-conversion visuals, hand-selected for global campaigns and legally indemnified.</p>
            <div className="mt-6 md:mt-7 w-full flex justify-center">
              <HeroSearchPill testId="cf-hero-search" />
            </div>
          </div>
        </section>

        {/* ============ HAND-PICKED ============ */}
        <section data-testid="cf-handpicked" className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <SectionHeader eyebrow="Signature series" title="Hand-picked by professionals" desc="Four distinctive families curated by our senior art directors." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {HAND_PICKED.map((it, i) => (
              <Card key={it.key} item={it} thumb={thumbs[it.bg]} size="lg" index={i} />
            ))}
          </div>
        </section>

        {/* ============ CATEGORIES (15) ============ */}
        <section data-testid="cf-categories" className="max-w-[1600px] mx-auto px-6 md:px-10 pb-14 md:pb-20">
          <SectionHeader eyebrow="Browse deep" title="Categories" desc="Fifteen focused entry points into the archive." />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5 md:gap-3">
            {CATEGORIES.map((it) => (
              <Card key={it.title} item={{ ...it, key: it.title }} thumb={thumbs[it.bg]} size="sm" />
            ))}
          </div>
        </section>

        {/* ============ TRENDING (4) ============ */}
        <section data-testid="cf-trending" className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20">
          <SectionHeader eyebrow="This season" title="Trending collections" desc="What creative directors are pulling right now." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {TRENDING.map((it) => (
              <Card key={it.title} item={{ ...it, key: it.title }} thumb={thumbs[it.bg]} size="md" />
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
